import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import Heading from 'atoms/Heading';
import Text from 'atoms/Text';
import Button from 'atoms/Button';

// What survives the trial ending, said plainly. A merchant who finds their
// store shut assumes the worst, and the first question is always whether the
// work they put in is still there.
const KEPT = ['Your products and categories', 'Your customers and their orders', 'Your store settings'];

const CheckIcon = () => (
  <svg
    className="w-5 h-5 shrink-0 text-success-100 mt-0.5"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <polyline points="9 12 11 14 15 10" />
  </svg>
);

/**
 * Deliberately not built on molecules/Modal: that component scales its card by
 * `0.85 / devicePixelRatio`, which renders it at 42.5% on any retina screen.
 * A full-screen block that a merchant has to read and act on cannot be the
 * thing that arrives half-size.
 *
 * Not dismissible either — no close control, no backdrop click, no Escape. The
 * account is off; dismissing this would leave the merchant clicking around an
 * app where nothing works.
 */
const PRIMARY_ACTION_ID = 'choosePlanGateAction';

const ChoosePlanGateModal = ({ canChoosePlan, onChoosePlan, onLogout }) => {
  useEffect(() => {
    // The page behind must not scroll under the block.
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    // By id rather than a ref: the Button atom is a plain component and does
    // not forward one.
    document.getElementById(PRIMARY_ACTION_ID)?.focus();

    return () => {
      document.body.style.overflow = overflow;
    };
  }, []);

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black-900 bg-opacity-50 px-4 py-6 animate-fade_in"
      role="dialog"
      aria-modal="true"
      // The Heading atom does not pass an id through, so the dialog is named
      // directly rather than pointing at an element that would never carry it.
      aria-label="Your trial has ended"
    >
      <div className="w-full max-w-[560px] max-h-full overflow-y-auto rounded-2xl bg-white p-6 shadow-card sm:p-8">
        {/* The atoms' default sizes are tuned for dense screens; this is a
            full-screen block a merchant has to read, so the title and lead
            are stepped up rather than left at dashboard scale. */}
        <Heading type="h2" className="text-primary-900 font-semibold !text-lg xl:!text-xl 2xl:!text-2xl">
          Your trial has ended
        </Heading>

        <Text
          variant="body"
          textColor="text-secondary-900"
          className="mt-3 !text-sm xl:!text-base"
        >
          Your store has stopped accepting new orders. Nothing has been deleted:
        </Text>

        <ul className="mt-4 flex flex-col gap-3">
          {KEPT.map((item) => (
            <li key={item} className="flex items-start gap-3">
              <CheckIcon />
              <Text variant="body" textColor="text-secondary-900">
                {item}
              </Text>
            </li>
          ))}
        </ul>

        <Text variant="bodySmall" textColor="text-secondary-100" className="mt-5">
          {canChoosePlan
            ? 'Choose a plan to reopen your store — everything comes back exactly as you left it.'
            : 'Contact your account owner to choose a plan and reopen the store.'}
        </Text>

        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end">
          <Button
            id="choosePlanGateLogout"
            varient="ghost"
            type="button"
            showIcon={false}
            btnClass="cursor-pointer !text-sm 2xl:!text-base !text-secondary-100 hover:!text-secondary-900 transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-indigo-500"
            onClick={onLogout}
          >
            Log out
          </Button>

          {canChoosePlan && (
            <Button
              id={PRIMARY_ACTION_ID}
              varient="mediumContained"
              type="button"
              showIcon={false}
              btnClass="cursor-pointer w-full sm:w-auto !text-sm 2xl:!text-base transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-indigo-500"
              onClick={onChoosePlan}
            >
              Choose a plan
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

ChoosePlanGateModal.propTypes = {
  canChoosePlan: PropTypes.bool,
  onChoosePlan: PropTypes.func.isRequired,
  onLogout: PropTypes.func.isRequired,
};

export default ChoosePlanGateModal;
